import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Popover,
  TextField,
  MenuItem,
  Button,
} from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import NetworkCheckIcon from "@material-ui/icons/NetworkCheck";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import { green } from "@material-ui/core/colors";
import { useVoice } from "../../context/Voice/VoiceContext";
import { useSoftphone } from "../../context/Voice/SoftphoneContext";
import { i18n } from "../../translate/i18n";
import { isBsuidNumber } from "../../helpers/formatContactIdentifier";

/**
 * Call button (ticket header + composer). The dial MODE is AUTOMATIC — browser
 * softphone when configured + WebRTC-capable, otherwise the phone bridge. A
 * preflight button runs a FULL browser self-test (mic → token → Device register)
 * and shows exactly where the chain stops in this browser.
 */
const Row = ({ label, res }) => {
  const ok = res && res.ok;
  const pending = !res;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 13 }}>
      {pending ? (
        <CircularProgress size={15} />
      ) : ok ? (
        <CheckCircleIcon style={{ fontSize: 17, color: green[600] }} />
      ) : (
        <ErrorIcon style={{ fontSize: 17, color: "#d32f2f" }} />
      )}
      <span style={{ fontWeight: 700, minWidth: 64 }}>{label}</span>
      <span style={{ opacity: 0.85 }}>{res ? res.message : "בודק…"}</span>
    </div>
  );
};

const VoiceCallButton = ({ ticket, iconClassName }) => {
  const { status, activeCall, startCall } = useVoice();
  const softphone = useSoftphone() || {};
  const {
    supported,
    runPreflight,
    runAudioDiagnostics,
    listMics,
    availableMics,
    selectedMicId,
    setSelectedMicId,
  } = softphone;
  const [anchor, setAnchor] = useState(null);
  const [pf, setPf] = useState(null); // { mic, token, register }
  const [running, setRunning] = useState(false);
  const [diag, setDiag] = useState(null); // { steps, verdict }
  const [diagRunning, setDiagRunning] = useState(false);

  const softphoneConfigured = Boolean(status && status.softphoneAvailable);
  const useSoftphoneMode = softphoneConfigured && supported === true;

  if (!status || !status.enabled || !status.outgoingEnabled) return null;

  const number = ticket && ticket.contact && ticket.contact.number;
  // WhatsApp Username/BSUID (Meta 2026): a "wau_<bsuid>" contact has no phone —
  // dialing the synthetic key is invalid, so keep the call button disabled.
  const disabled = !number || isBsuidNumber(number) || Boolean(activeCall);

  const doPreflight = async () => {
    if (typeof runPreflight !== "function" || running) return;
    setRunning(true);
    setPf({ mic: null, token: null, register: null });
    try {
      const r = await runPreflight();
      setPf(r);
    } catch (err) {
      setPf({
        mic: { ok: false, message: "בדיקה נכשלה" },
        token: { ok: false, message: "—" },
        register: { ok: false, message: "—" },
      });
    } finally {
      setRunning(false);
    }
  };

  const runCheck = async (e) => {
    setAnchor(e.currentTarget);
    setDiag(null);
    if (typeof listMics === "function") listMics(); // populate the device list
    doPreflight();
  };

  const doDiagnostics = async () => {
    if (typeof runAudioDiagnostics !== "function" || diagRunning) return;
    setDiagRunning(true);
    setDiag(null);
    try {
      setDiag(await runAudioDiagnostics());
    } catch (err) {
      setDiag({ steps: [], verdict: "בדיקת האודיו נכשלה" });
    } finally {
      setDiagRunning(false);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    // SOFTPHONE: the agent talks FROM the system (device mic, like WhatsApp) and
    // the customer gets a direct call — NO connecting phone, NO bridge. This is
    // the model the user wants. On a browser/OS WITHOUT WebRTC support
    // (useSoftphoneMode false) we fall back to a server-side "direct" call so the
    // button still works everywhere instead of erroring.
    startCall({
      toNumber: number,
      contactId: ticket.contact.id,
      ticketId: ticket.id,
      purpose: "",
      mode: useSoftphoneMode ? "softphone" : "direct",
      contactName: ticket.contact.name,
      contactAvatar: ticket.contact.profilePicUrl,
    });
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {/* Mic-check icon hidden per request — it cluttered the composer bar.
          (The diagnostics still exist behind this flag if ever needed again.) */}
      {false && useSoftphoneMode && (
        <>
          <Tooltip title="בדיקת חיוג מהדפדפן (מיקרופון · טוקן · Device)">
            <IconButton
              size="small"
              onClick={runCheck}
              aria-label="בדיקת softphone"
              style={iconClassName ? undefined : { padding: 6 }}
            >
              <NetworkCheckIcon fontSize="small" className={iconClassName} />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(anchor)}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <div style={{ padding: 14, minWidth: 300, direction: "rtl" }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                בדיקת חיוג מהדפדפן
              </div>

              {/* Microphone selector */}
              <TextField
                select
                size="small"
                variant="outlined"
                fullWidth
                label="מיקרופון"
                value={selectedMicId || ""}
                onChange={(e) => setSelectedMicId && setSelectedMicId(e.target.value)}
                style={{ marginBottom: 10 }}
              >
                <MenuItem value="">ברירת מחדל של המערכת</MenuItem>
                {(availableMics || []).map((m, i) => (
                  <MenuItem key={m.deviceId || i} value={m.deviceId}>
                    {m.label || `מיקרופון ${i + 1}`}
                  </MenuItem>
                ))}
              </TextField>

              <Row label="מיקרופון" res={pf && pf.mic} />
              <Row label="טוקן" res={pf && pf.token} />
              <Row label="Device" res={pf && pf.register} />

              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={doPreflight}
                  disabled={running}
                  startIcon={running ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  בדוק שוב
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={doDiagnostics}
                  disabled={diagRunning}
                  startIcon={diagRunning ? <CircularProgress size={14} /> : null}
                >
                  בדיקת אודיו מתקדמת
                </Button>
              </div>

              {diag && (
                <div style={{ marginTop: 10, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 8 }}>
                  {(diag.steps || []).map((s, i) => (
                    <Row key={i} label={s.label} res={{ ok: s.ok, message: s.detail }} />
                  ))}
                  {diag.verdict && (
                    <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 8, color: diag.verdict.includes("תקין") ? "#2e7d32" : "#a32d2d" }}>
                      ⮕ {diag.verdict}
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>
                טוקן + Device לא תלויים במיקרופון — אם הם ✓ והמיקרופון ✗, הבעיה רק במיקרופון/דפדפן.
              </div>
            </div>
          </Popover>
        </>
      )}

      <Tooltip title={i18n.t("voice.callButton")}>
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={disabled}
            style={{ color: green[600], padding: 6 }}
            aria-label={i18n.t("voice.callButton")}
          >
            <PhoneIcon
              fontSize="small"
              className={iconClassName}
              style={iconClassName ? { color: green[600] } : undefined}
            />
          </IconButton>
        </span>
      </Tooltip>
    </span>
  );
};

export default VoiceCallButton;
